import {
  ChangeEvent,
  useRef,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import { BillingSubType, BillingType } from "./List/BillingTypes/types"
import { CellRendererProps, useCell } from "components/Bable"
import { css, cx } from "@emotion/css"
import { useHistory, useLocation } from "react-router-dom"

import Button from "ui/buttons/Button"
import { ErrorsMap } from "api/http/utils"
import GhostTextInput from "ui/inputs/GhostTextInput"
import { MoreButtonBlock } from "components/buttons/MoreButtonBlock"
import { Popover } from "@material-ui/core"
import { useAuthenticated } from "providers/A12n"
import { useContextOptions } from "./List/index"
import { useI18n } from "providers/I18n"
import { useRow } from "components/Bable/data/useRow"

const EditableCell = (props: CellRendererProps) => {
  const { t } = useI18n()
  const { dataKey, rowId } = props
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)
  const { onUpdate } = useContextOptions()
  const { hasAuthority } = useAuthenticated()
  const [value, setValue, error] = useCell<BillingType>(
    rowId,
    dataKey as keyof BillingType,
    "",
  )
  const isChosenKey = dataKey === "description" || dataKey === "name"
  const smallText = !(isChosenKey && (value as string)?.length > 16)

  const handlePopoverOpen = (
    event: ReactMouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePopoverClose = () => {
    setAnchorEl(null)
  }

  const row = useRow<BillingType>(rowId)
  let old = useRef(value)

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
  }

  const onBlur = () => {
    const hasValueDiff = old.current !== value
    if (dataKey !== undefined && hasValueDiff) {
      onUpdate(row.value, dataKey, value).then(() => (old.current = value))
    }
  }

  return (
    <>
      <GhostTextInput
        value={value as string}
        onChange={onChange}
        onBlur={onBlur}
        className={cx(styles.input, styles.ghostInput, {
          [styles.error]: !!error,
        })}
        disabled={!hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])}
      />
      {!smallText ? (
        <>
          <span
            onMouseEnter={handlePopoverOpen}
            onMouseLeave={handlePopoverClose}
            color="primary"
            className={cx("BodyBody-2Primary-mainRegular", styles.showMore)}
          >
            {t("admin.billingTypes.subBillingType.viewMore")}
          </span>
          {open ? (
            <Popover
              open={open}
              anchorEl={anchorEl}
              PaperProps={{
                onMouseEnter: handlePopoverOpen,
                onMouseLeave: handlePopoverClose,
              }}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              className={styles.popover}
              classes={{ paper: styles.paper }}
            >
              <div className={styles.tooltip}>
                <span className={"BodyBody-2BlackRegular"}>{value}</span>
              </div>
            </Popover>
          ) : null}
        </>
      ) : null}
    </>
  )
}

const ListCell = (props: CellRendererProps) => {
  const history = useHistory()
  const { pathname } = useLocation()
  const { t } = useI18n()
  const { dataKey, rowId } = props
  const [value] = useCell<BillingType>(rowId, "billingSubtypes", "")
  const row = useRow<BillingType>(rowId)
  const { hasAuthority } = useAuthenticated()
  const subBillingTypes = !!(value as BillingSubType[] | undefined)?.length
    ? (value as BillingSubType[])
    : []

  const [openMore, setOpenMore] = useState<boolean>(false)
  const [errors] = useState<ErrorsMap | undefined>({})

  const onOpenMore = () => {
    setOpenMore(true)
  }

  const onOpen = () => {
    const billingTypeId = row.value?.id
    if (!!billingTypeId) {
      const params = new URLSearchParams({ billingTypeId })
      history.push({ pathname, search: params.toString() })
    }
  }

  const disabled = !hasAuthority(["AUTH_ACCESS_MARKET_FINANCE"])

  const hasSubTypes = subBillingTypes.length !== 0

  const showFirstError = !!errors ? errors[Object.keys(errors ?? {})[0]] : ""

  return openMore ? (
    <>
      <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
        {value}
      </div>
      {!disabled ? (
        <Button
          onClick={onOpen}
          color="primary"
          className={cx(styles.textButton, {})}
          fullWidth={false}
        >
          {t("admin.billingTypes.subBillingType.edit")}
        </Button>
      ) : null}
    </>
  ) : (
    <>
      {hasSubTypes ? (
        <>
          <MoreButtonBlock
            options={subBillingTypes}
            error={showFirstError}
            onOpen={onOpenMore}
            dataKey={dataKey}
            name="admin.billingTypes"
            disabled={true}
            slice={3}
          />
          {!disabled ? (
            <Button
              onClick={onOpen}
              color="primary"
              className={styles.textButton}
              fullWidth={false}
            >
              {t("admin.billingTypes.subBillingType.edit")}
            </Button>
          ) : null}
        </>
      ) : (
        <>
          {!disabled ? (
            <Button
              onClick={onOpen}
              color="primary"
              className={styles.textButton}
              fullWidth={false}
            >
              {t("admin.billingTypes.subBillingType.add")}
            </Button>
          ) : null}
        </>
      )}
      {!!errors ? (
        <span className={cx("BodyBody-xsRedRegular", styles.errorMsg)}>
          {t(showFirstError)}
        </span>
      ) : null}
    </>
  )
}

const styles = {
  readonly: css`
    margin: var(--spacing-xxs);
    display: flex;
    flex: 1;
  `,

  input: css`
    font-size: 13px;
    :hover {
      cursor: pointer;
    }
    .MuiOutlinedInput-input {
      font-size: 13px;
    }
  `,
  ghostInput: css`
    padding: var(--spacing-xxs);
    :focus-within {
      border: 2px solid var(--primary-main, teal);
    }
  `,
  error: css`
    border: 1px solid var(--red);
  `,
  errorMsg: css`
    margin: 0 5px;
  `,
  textButton: css`
    --btn-padding: 0;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-active: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    text-transform: capitalize;
    transition: none;
    &:focus {
      box-shadow: none;
    }
    &:hover {
      background-color: transparent;
      border: 1px solid transparent;
    }
    display: flex;
    margin-left: auto;
    margin-right: var(--spacing-xs);
    flex-direction: flex-end;
  `,
  primary: css`
    color: var(--primary-main);
  `,
  grid: css`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: var(--spacing-xxs);
  `,
  container: css`
    display: inline-flex;
    margin-right: auto;
    align-self: flex-center;
    overflow: auto;
    text-align: justify;
    max-width: 200px;
  `,
  paper: css`
    pointer-events: auto;
  `,
  popover: css`
    margin-top: var(--spacing-xxs);
    pointer-events: none;
  `,
  showMore: css`
    display: flex;
    margin-left: auto;
    cursor: pointer;
    margin-right: var(--spacing-xs);
    flex-direction: flex-end;
  `,
  tooltip: css`
    padding: var(--spacing-xs);
    border-radius: 4px;
    box-shadow: 0 0 18px 0 var(--light-grey);
  `,
}

const commonColumns = () => [
  {
    id: "name",
    Header: "label",
    dataKey: "name",
    Cell: EditableCell,
  },
  {
    id: "semanticName",
    Header: "semantic",
    dataKey: "semanticName",
    Cell: (props: CellRendererProps) => {
      const { dataKey, rowId } = props
      const [value] = useCell<BillingType>(
        rowId,
        dataKey as keyof BillingType,
        "",
      )
      return (
        <div className={cx("BodyBody-2BlackRegular", styles.readonly)}>
          {value}
        </div>
      )
    },
  },
  {
    id: "billingSubtypes",
    Header: "sub-billing type",
    dataKey: "billingSubtypes",
    resizable: true,
    Cell: ListCell,
  },
  {
    id: "description",
    Header: "description",
    dataKey: "description",
    resizable: true,
    Cell: EditableCell,
  },
]

export const COLUMNS = (actionAllowed: boolean) => {
  return actionAllowed
    ? [...commonColumns().slice(0, 4), ...commonColumns().slice(4)]
    : commonColumns()
}
